import React, { Fragment, useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { MoreOutlined } from '@ant-design/icons';
import { Dropdown, Table } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import ModalActions from 'components/ModalActions';
import type { IAccessPolicyUserList } from 'interfaces/IAccessPolicies';
import accessControlService from 'v2/services/access-control';

import { useAccessPolicyUsers } from '../useAccessPolicyUsers';

type AccessUser = IAccessPolicyUserList[0];

const TAB_PANEL_CLASS =
  'w-full overflow-hidden rounded-sm bg-white shadow-[0px_2px_1px_-1px_rgba(0,0,0,0.2),0px_1px_1px_0px_rgba(0,0,0,0.14),0px_1px_3px_0px_rgba(0,0,0,0.12)]';

interface IProps {
  onEdit: (user: AccessUser) => void;
}

const AccessUsers: React.FC<IProps> = ({ onEdit }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState<AccessUser | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const { users, isFetching, isError, refetch } = useAccessPolicyUsers();

  const handleEdit = useCallback(
    (user: AccessUser) => {
      onEdit(user);
    },
    [onEdit]
  );

  const handleAskDelete = useCallback((user: AccessUser) => {
    setCurrentUser(user);
    setShowDeleteConfirmation(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
    setCurrentUser(null);
  }, []);

  const handleDelete = useCallback(async () => {
    const userToDelete = currentUserRef.current;
    setShowDeleteConfirmation(false);
    setIsDeleting(true);
    setCurrentUser(null);

    try {
      await accessControlService.deleteUser(userToDelete.id);
      toast.success('Sucesso, usuário removido');
      refetch();
      setIsDeleting(false);
    } catch (err) {
      setIsDeleting(false);
      toast.error('Ops, erro na remoção do usuário');
    }
  }, [refetch]);

  const getMenuItems = useCallback(
    (item: AccessUser): MenuProps['items'] => [
      {
        key: 'edit',
        label: 'Editar',
        onClick: () => handleEdit(item)
      },
      {
        key: 'delete',
        label: 'Remover',
        onClick: () => handleAskDelete(item)
      }
    ],
    [handleEdit, handleAskDelete]
  );

  const columns: ColumnsType<AccessUser> = [
    {
      title: 'Usuário',
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'E-mail',
      key: 'email',
      dataIndex: 'email'
    },
    {
      title: 'Grupo',
      key: 'group_name',
      dataIndex: 'group_name'
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'right',
      render: (_, item) => (
        <Dropdown menu={{ items: getMenuItems(item) }} trigger={['click']}>
          <span className='cursor-pointer'>
            <MoreOutlined />
          </span>
        </Dropdown>
      )
    }
  ];

  return (
    <Fragment>
      {!!isError && (
        <div className={TAB_PANEL_CLASS}>
          <p>Um erro aconteceu...</p>
        </div>
      )}

      {!isError && (
        <div className={TAB_PANEL_CLASS}>
          <Table
            columns={columns}
            dataSource={Array.isArray(users) ? users : []}
            rowKey='id'
            loading={isFetching || isDeleting}
            pagination={false}
            scroll={{ x: columns.length * 100 }}
            locale={{ emptyText: 'Nenhum registro encontrado.' }}
          />
        </div>
      )}

      <ModalActions
        open={showDeleteConfirmation}
        onClose={handleCancelDelete}
        onOk={handleDelete}
        title='Atenção'
        buttonOk='Continuar'
      >
        {currentUser && <p>Deseja realmente remover o usuário {currentUser.name}?</p>}
      </ModalActions>
    </Fragment>
  );
};

export default AccessUsers;
